import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { HiOutlineChevronRight } from "react-icons/hi";
import PropTypes from "prop-types";
import NewPLPCard from "@/components/ProductListing/PLPCards/index";
import FilterAccordion from "@/components/ProductListing/FilterAccordion";
import FilterItems from "@/components/ProductListing/FilterItems";
import CompareProducts from "@/components/CustomComponents/CompareProducts";
import {
  buildFacetObjectFromLabelsAndFields,
  buildFqParams,
} from "@/components/ProductListing/helper";
import BackToTop from "@/components/backToTop";
import Loader from "@/components/loader";
import { formatListingData } from "@/services/server/plpHelper";
import { getPlpProcessedData } from "@/services/server/plpResponseProcessor";
import {
  APIM_BASE_URL,
  DEFAULT_VISIBLE_FILTERS,
  lwAppName,
  masterPLPFL,
  PLP_END_POINT_CLIENT,
  searchPLPEndPoint,
  SSR_PLP_FL,
  structuredPLPProfileName,
} from "@/constants";
import { apim } from "@/constants/api";
import NotFoundSection from "../detailsComponent/NotFound";
import { useContentfulTranslations } from "@/utils/contentfulTranslations";

const ShowAllProducts = ({
  params,
  locale = "en",
  categoryProductData,
  compareMappingsData = {},
}) => {
  const defaultLocale =
    locale === "default" ? process.env.NEXT_PUBLIC_DEFAULT_LOCALE : locale;
  const apiLocale =
    locale === "default" ? process.env.NEXT_PUBLIC_LW_LANGUAGE : locale;

  const { SHOWALL_LABELS, staticLabelsPLP } = useContentfulTranslations();
  const [isHovering, setIsHovering] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [skuId, setSkuId] = useState("");
  const [newPLPCards, setNewPLPCards] = useState([]);
  const [showCategoryList, setShowCategoryList] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [masterPlpGroupData, setMasterPlpGroupData] = useState([]);
  const [selectedSkuId, setSelectedSkuId] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [mainCategoryTitle, setMainCategoryTitle] = useState(
    params?.slug || ""
  );
  const [mappedFilterFacets, setMappedFilterFacets] = useState({});
  const [selectedFilter, setSelectedFilter] = useState({});
  const [filterSelectedOnAction, setFilterSelectedOnAction] = useState({});
  const [selectedFilterCount, setSelectedFilterCount] = useState({});
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [openAccordions, setOpenAccordions] = useState([]);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const hasHydratedFromUrl = useRef(false);

  const prepareCategoryList = (productCategories = [], categoryLabels = {}) => {
    return productCategories
      .filter((_, i) => i % 2 === 0)
      .map((value, i) => ({
        value: categoryLabels?.[value] ?? value,
        count: productCategories[i * 2 + 1],
        forLink: value,
      }));
  };

  const prepareProductData = async (data = {}) => {
    const {
      facet_counts: {
        facet_fields: facetFields = {},
        facet_ranges: facetRanges = {},
      } = {},
      fusion: {
        category_labels = {},
        facet_labels: facetlabels = {},
        color_swatch_labels: colorSwatchLabels = {},
      } = {},
      grouped: { ProductCategory_s: { groups = [] } = {} } = {},
      response: { numFound = 0 } = {},
    } = data || {};
    const productCategories = facetFields?.ProductCategory_s || [];
    const filterFacets = buildFacetObjectFromLabelsAndFields(
      { ...facetlabels },
      { ...facetFields },
      colorSwatchLabels,
      facetRanges
    );
    setMappedFilterFacets(filterFacets);
    setTotalProductCount(numFound);
    const plpGroupData = await Promise.all(
      groups.map(async element => {
        const formattedData = await formatListingData(data, false);
        formattedData.data = element.doclist?.docs || [];
        const processedData = await getPlpProcessedData(formattedData);
        return {
          groupValue:
            category_labels?.[element.groupValue] ?? element.groupValue,
          cardItem: processedData.data,
          forLink: element.groupValue,
        };
      })
    );
    if (Object.keys(category_labels)?.length) {
      const matchedKey = Object.keys(category_labels).find(
        key => key.toLowerCase() === params?.slug.toLowerCase()
      );
      setMainCategoryTitle(category_labels[matchedKey]);
    }
    setIsLoading(false);
    setCategoryList(prepareCategoryList(productCategories, category_labels));
    setMasterPlpGroupData(plpGroupData);
  };

  const executeMasterPLP = async () => {
    const { slug = "" } = params || {};
    const category = slug?.charAt(0).toUpperCase() + slug.slice(1);
    const profileName = structuredPLPProfileName(category);
    const query = `q=*:*&fq=language_s:${encodeURIComponent(apiLocale)}&profilename=${profileName}&fl=${masterPLPFL.join(",")}&collections=${lwAppName}&rows=50&masterPLP=true`;
    const url = `${APIM_BASE_URL}${searchPLPEndPoint}?${query}`;
    apim
      .get(url)
      .then(response => {
        if (response?.data) {
          prepareProductData(response.data);
        }
      })
      .catch(err => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    executeMasterPLP();
  }, []);

  // Hydrate selected filters from URL
  useEffect(() => {
    if (hasHydratedFromUrl.current) return;
    if (typeof window === "undefined") return;
    const facetEntries = Object.entries(mappedFilterFacets);
    if (facetEntries.length === 0) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (![...urlParams.keys()].length) {
      hasHydratedFromUrl.current = true;
      return;
    }

    const hydratedSelected = {};
    const hydratedCount = {};
    const hydratedOnAction = {};
    const hydratedOpen = [];

    facetEntries.forEach(([label, { field, options = [] }]) => {
      if (!urlParams.has(label)) return;
      const value = urlParams.get(label);
      if (value === null || value === "") return;
      const matched = options.find(opt => String(opt?.value) === String(value));
      hydratedSelected[label] = value;
      hydratedCount[label] = matched?.count ?? 0;
      hydratedOnAction[field] = value;
      hydratedOpen.push(label);
    });

    hasHydratedFromUrl.current = true;

    if (Object.keys(hydratedOnAction).length === 0) return;

    setSelectedFilter(hydratedSelected);
    setSelectedFilterCount(hydratedCount);
    setFilterSelectedOnAction(hydratedOnAction);
    setOpenAccordions(prev => Array.from(new Set([...prev, ...hydratedOpen])));
    executeFilteredPLP(hydratedOnAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedFilterFacets]);

  const handleMouseOver = id => {
    setSkuId(id);
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
    setSkuId("");
  };

  const handleModalOpen = (sku = "") => {
    setModalOpen(true);
    setSelectedSkuId(sku);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const formatRedirectionUrl = (urlName = "") => {
    const { slug = "" } = params || {};
    const pageName = slug?.split("/")?.[0].toLocaleLowerCase();
    const modifiedUrlName =
      urlName === "Showering" ? "bathroom-showering" : urlName;
    const redirectUrl = `${pageName}/Categories/${modifiedUrlName?.replace(/ /g, "-")?.toLocaleLowerCase()}`;
    return redirectUrl;
  };

  const slugUrl = Array.isArray(params?.slug)
    ? params.slug.join("/")
    : params?.slug || "";
  const linkProps = slugUrl.split("/")[0];

  const handleAccordionToggle = label => {
    setOpenAccordions(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    );
  };

  const handleReloadNavigation = e => {
    e.preventDefault();
    const fullUrl = e.currentTarget.href;
    const cleanUrl = fullUrl.split(/[?#]/)[0];
    globalThis.location.href = cleanUrl;
  };

  const updateUrlQuery = (aliasFieldName = "", value = "") => {
    if (typeof window === "undefined" || !aliasFieldName) return;
    const params = new URLSearchParams(window.location.search);
    if (value === "" || value === undefined || value === null) {
      params.delete(aliasFieldName);
    } else {
      params.set(aliasFieldName, value);
    }
    const queryString = params.toString();
    const newRelativePathQuery = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    window.history.replaceState(null, "", newRelativePathQuery);
  };

  const filterSelect = (filterRequest = {}, filterCount = {}) => {
    const [field, rawValue] = Object.entries(filterRequest)[0] || [];
    const [aliasFieldName, countValue] = Object.entries(filterCount)[0] || [];
    if (!field) return;
    const next = { ...selectedFilter };
    const nextCount = { ...selectedFilterCount };
    const nextOnAction = { ...filterSelectedOnAction };
    if (rawValue === "" || rawValue === undefined) {
      delete next[aliasFieldName];
      delete nextCount[aliasFieldName];
      delete nextOnAction[field];
      updateUrlQuery(aliasFieldName, "");
    } else {
      next[aliasFieldName] = rawValue;
      nextCount[aliasFieldName] = countValue;
      nextOnAction[field] = rawValue;
      updateUrlQuery(aliasFieldName, rawValue);
    }
    setSelectedFilter(next);
    setSelectedFilterCount(nextCount);
    setFilterSelectedOnAction(nextOnAction);
    executeFilteredPLP(nextOnAction);

    if (typeof window !== "undefined") {
      const target = document.getElementById("showall-plp-section");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const executeFilteredPLP = (filtersByField = {}) => {
    const { slug = "" } = params || {};
    const category = slug?.charAt(0).toUpperCase() + slug.slice(1);
    const profileName = structuredPLPProfileName(category);

    // If nothing selected, reload the master PLP (grouped) view.
    if (!Object.keys(filtersByField).length) {
      setIsLoading(true);
      executeMasterPLP();
      return;
    }

    const fqParams = buildFqParams(filtersByField, apiLocale);
    const paramsFq = new URLSearchParams();
    paramsFq.append("profilename", profileName);
    paramsFq.append("collections", lwAppName);
    fqParams.forEach(fq => paramsFq.append("fq", fq));
    paramsFq.append("fl", SSR_PLP_FL.join(","));
    paramsFq.append("rows", "30");
    paramsFq.append("start", "0");

    const url = `${APIM_BASE_URL}${PLP_END_POINT_CLIENT}?${paramsFq.toString()}`;

    setIsLoading(true);
    apim
      .get(url)
      .then(async response => {
        const { data = null } = response || {};
        if (!data) {
          setIsLoading(false);
          return;
        }
        const {
          facet_counts: {
            facet_fields: facetFields = {},
            facet_ranges: facetRanges = {},
          } = {},
          fusion: {
            facet_labels: facetlabels = {},
            color_swatch_labels: colorSwatchLabels = {},
          } = {},
          response: { numFound = 0 } = {},
        } = data;

        const filterFacets = buildFacetObjectFromLabelsAndFields(
          { ...facetlabels },
          { ...facetFields },
          colorSwatchLabels,
          facetRanges
        );

        if (Object.keys(filterFacets).length > 0) {
          setMappedFilterFacets(filterFacets);
        }
        setTotalProductCount(numFound);

        const formattedData = await formatListingData(data, false);
        const processedData = await getPlpProcessedData(formattedData);
        const { data: plpListData = [] } = processedData || {};
        setMasterPlpGroupData([
          {
            groupValue: "",
            cardItem: plpListData,
            forLink: "",
          },
        ]);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const filterEntries = Object.entries(mappedFilterFacets);
  const visibleFilterEntries = showAllFilters
    ? filterEntries
    : filterEntries.slice(0, DEFAULT_VISIBLE_FILTERS);

  const hasAnyProducts = masterPlpGroupData.some(
    ({ cardItem = [] } = {}) => Array.isArray(cardItem) && cardItem.length > 0
  );

  if (!isLoading && !hasAnyProducts) {
    return <NotFoundSection />;
  }

  return (
    <>
      {isLoading && <Loader loading={isLoading} />}
      <section
        id="showall-plp-section"
        className="max-w-screen-lg mx-auto bg-white text-[#232323] overflow-auto px-[10px]"
      >
        <div className="pt-[50px] md:pb-[22px] mb-[8px] mx-[10px]">
          <h2 className="font-['Helvetica-Light',_Helvetica,_sans-serif] text-[25px] md:text-[30px] leading-[38px] font-normal uppercase">
            {mainCategoryTitle}
          </h2>
        </div>
        <div className="border-b-1 border-[#bebebe] md:hidden"></div>
        <div className="flex flex-wrap md:flex-nowrap w-full flex-col md:flex-row  md:gap-[80px]">
          <div className="flex flex-col md:w-1/4 md:mb-[38px] grow w-full cursor-pointer">
            <div className="md:hidden flex justify-center mb-[20px] bg-[fff] text-[#232323] border-2 my-[10px] rounded-md border-[#dbdbdb]">
              <button
                type="button"
                className="p-[20px] text-[#232323] text-[16px] leading-[16px] font-[400] font-[helveticaRoman,_Helvetica,_sans-serif]"
                onClick={() => setShowCategoryList(!showCategoryList)}
              >
                {SHOWALL_LABELS?.[defaultLocale]?.categoryButton}
              </button>
            </div>
            <div
              className={`${!showCategoryList ? `hidden` : `block`} md:block`}
            >
              <div className="flex mx-[10px]">
                <h3 className="font-['Helvetica-Light',_Helvetica,_sans-serif] tracking-tight leading-[22px] font-[400] text-[22px] pt-[12px]">
                  {SHOWALL_LABELS?.[defaultLocale]?.category}:
                </h3>
              </div>
              <div className="border-b-1 border-[#bebebe] my-[12px]"></div>
              <ul className="mx-[10px]">
                {categoryList?.map((item, index) => (
                  <li className="py-[11px]" key={item?.value || index}>
                    <a
                      href={formatRedirectionUrl(item.forLink)}
                      className="font-['Helvetica-Light',_Helvetica,_sans-serif] text-[14px] leading-[14px] font-[400] text-[#232323] hover:!no-underline"
                    >
                      {item.value}{" "}
                      <span className="text-[#999]">({item.count})</span>
                    </a>
                  </li>
                ))}
              </ul>
              <div className="border-b-1 border-[#bebebe]"></div>
              {visibleFilterEntries.length > 0 ? (
                <div className="pt-[30px] mx-[3px]">
                  <div className="flex justify-between pb-[22px] mt-[14px] mb-[22px] border-b-1">
                    <div className="flex">
                      <h3 className="font-['Helvetica-Light',_Helvetica,_sans-serif] leading-tight text-[22px] font-normal">
                        {staticLabelsPLP?.[defaultLocale]?.filterby}
                      </h3>
                    </div>
                    <div className="flex mt-[5px] text-[#3b659c] text-[13px]">
                      <Link
                        href={linkProps}
                        as={linkProps}
                        onClick={handleReloadNavigation}
                        className="![text-decoration:none] cursor-pointer"
                      >
                        {staticLabelsPLP?.[defaultLocale]?.clearAll}
                      </Link>
                    </div>
                  </div>
                  {visibleFilterEntries.map(
                    ([label, { field, options, type = "" }]) => {
                      let filterTypeValue =
                        type || (field === "ColorFinish_s" ? "image" : "list");
                      return (
                        <FilterAccordion
                          header={label}
                          key={label}
                          isOpen={openAccordions.includes(label)}
                          onToggle={() => handleAccordionToggle(label)}
                        >
                          <FilterItems
                            key={label}
                            filterOption={options}
                            filterType={filterTypeValue}
                            filterName={label}
                            filterField={field}
                            minRange={0}
                            maxRange={0}
                            filterSelect={filterSelect}
                            selectedFilter={selectedFilter}
                            selectedFilterCount={selectedFilterCount}
                            totalProductCount={totalProductCount}
                          />
                        </FilterAccordion>
                      );
                    }
                  )}
                  {filterEntries.length > DEFAULT_VISIBLE_FILTERS && (
                    <button
                      type="button"
                      className="cursor-pointer text-[#3b659c] text-sm mt-2"
                      onClick={() => setShowAllFilters(prev => !prev)}
                    >
                      {showAllFilters
                        ? staticLabelsPLP?.[defaultLocale]?.showLess
                        : staticLabelsPLP?.[defaultLocale]?.showMore}
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col md:w-3/4 w-full overflow-hidden mx-[10px] mb-5 pt-3 pb-5 grow">
            {masterPlpGroupData?.map((groupItem, index) => {
              const {
                cardItem = [],
                groupValue = "",
                forLink = "",
              } = groupItem;
              return (
                <div
                  className="flex flex-col"
                  key={groupValue || index}
                  id={groupValue}
                >
                  {Object.keys(filterSelectedOnAction).length === 0 && (
                    <a
                      href={formatRedirectionUrl(forLink)}
                      className="flex text-[#232323] hover:underline font-['Helvetica-Light',_Helvetica,_sans-serif] text-2xl leading-tight font-light py-[22px]"
                    >
                      <span>{groupValue} </span>
                      <span className="ml-[5px] mt-[3px]">
                        <HiOutlineChevronRight size={25} />
                      </span>
                    </a>
                  )}
                  <div className="flex flex-wrap">
                    {cardItem.length ? (
                      cardItem.map((item, index) => {
                        return Object.keys(filterSelectedOnAction).length >
                          0 ? (
                          <NewPLPCard
                            handleMouseOut={() => handleMouseOut()}
                            handleMouseOver={e => handleMouseOver(e)}
                            isHovering={isHovering}
                            selectedSkuId={selectedSkuId}
                            item={item}
                            handleModalOpen={(sku = "") => handleModalOpen(sku)}
                            handleModalClose={() => handleModalClose()}
                            modalOpen={modalOpen}
                            key={item?.sku}
                            defaultLocale={defaultLocale}
                            displayCompare={true}
                            locale={defaultLocale}
                            compareMappingsData={compareMappingsData}
                          />
                        ) : (
                          <NewPLPCard
                            handleMouseOut={() => handleMouseOut()}
                            handleMouseOver={e => handleMouseOver(e)}
                            isHovering={isHovering}
                            selectedSkuId={selectedSkuId}
                            item={item}
                            handleModalOpen={(sku = "") => handleModalOpen(sku)}
                            handleModalClose={() => handleModalClose()}
                            modalOpen={modalOpen}
                            key={item?.sku}
                            defaultLocale={defaultLocale}
                            displayCompare={false}
                            locale={defaultLocale}
                          />
                        );
                      })
                    ) : (
                      <div>
                        {staticLabelsPLP?.[defaultLocale]?.noProductFound}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <BackToTop topHeight={0} localeProp={defaultLocale} />
      {Object.keys(filterSelectedOnAction).length > 0 && <CompareProducts />}
    </>
  );
};

ShowAllProducts.propTypes = {
  params: PropTypes.object,
  defaultLocale: PropTypes.string,
  categoryProductData: PropTypes.shape({
    response: PropTypes.shape({
      "@search.facets": PropTypes.shape({
        RegionProductCategoryLocal: PropTypes.arrayOf(PropTypes.object),
      }),
      value: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
};

export default ShowAllProducts;
